import { useState, useRef, useEffect, useCallback } from "react";

interface UseEditedTabsReturn {
  editedTabs: Record<string, boolean>;
  setTabIsEdited: (key: string, value: boolean, cb?: () => void) => void;
  clearEditedTab: (key: string) => void;
  unsavedForms: string[];
}

export function useEditedTabs(): UseEditedTabsReturn {
  const [editedTabs, setEditedTabs] = useState<Record<string, boolean>>({});
  const onSaveCbRef = useRef<(() => void) | null>(null);

  const setTabIsEdited = useCallback(
    (key: string, value: boolean, cb?: () => void) => {
      if (editedTabs[key] !== value) {
        const newState = { ...editedTabs };
        newState[key] = value;
        setEditedTabs(newState);
        if (cb) {
          onSaveCbRef.current = cb;
        }
      }
    },
    [editedTabs]
  );

  const clearEditedTab = useCallback((key: string) => {
    setEditedTabs((prev) => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  }, []);

  const unsavedForms = Object.keys(editedTabs).filter((key) =>
    Boolean(editedTabs[key])
  );

  useEffect(() => {
    if (
      unsavedForms.length === 0 &&
      typeof onSaveCbRef.current === "function"
    ) {
      onSaveCbRef.current();
      onSaveCbRef.current = null;
    }
  }, [unsavedForms.length]);

  return {
    editedTabs,
    setTabIsEdited,
    clearEditedTab,
    unsavedForms,
  };
}
